import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { FiPlus } from 'react-icons/fi';
import { colors } from '../theme/modernTheme';
import { trustMastersAPI } from '../services/api';
import { showSuccess, showError } from '../utils/toast';
import TrustMasterList from '../components/TrustMasterList';
import PreviewModal from '../components/PreviewModal';
import DeleteConfirmationDialog from '../components/DeleteConfirmationDialog';

/**
 * Trust Masters Management Page
 * Main page component that manages state and coordinates all trust master operations
 */
export default function TrustMasters() {
  const navigate = useNavigate();

  // Modal states
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Preview state
  const [previewContent, setPreviewContent] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewFilename, setPreviewFilename] = useState('');
  const [previewTrustMasterId, setPreviewTrustMasterId] = useState(null);

  // Loading states
  const [deleting, setDeleting] = useState(false);

  // Refresh trigger for list
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Trust master to delete
  const [trustMasterToDelete, setTrustMasterToDelete] = useState(null);

  /**
   * Handle create new trust master
   */
  const handleCreateNew = () => {
    navigate('/trust-masters/new');
  };

  /**
   * Handle row click - navigate to detail page
   */
  const handleView = (trustMaster) => {
    navigate(`/trust-masters/${trustMaster.id}`);
  };

  /**
   * Handle edit trust master
   */
  const handleEdit = (trustMaster) => {
    navigate(`/trust-masters/${trustMaster.id}/edit`);
  };

  /**
   * Handle delete click
   */
  const handleDeleteClick = (trustMaster) => {
    setTrustMasterToDelete(trustMaster);
    setDeleteDialogOpen(true);
  };

  /**
   * Handle delete confirm
   */
  const handleDeleteConfirm = async () => {
    if (!trustMasterToDelete) return;

    try {
      setDeleting(true);
      await trustMastersAPI.delete(trustMasterToDelete.id);
      showSuccess('Trust master deleted successfully');
      setDeleteDialogOpen(false);
      setTrustMasterToDelete(null);
      // Trigger list refresh
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('Error deleting trust master:', error);
      showError(error?.error?.message || 'Failed to delete trust master');
    } finally {
      setDeleting(false);
    }
  };

  /**
   * Handle preview receipt
   */
  const handlePreviewReceipt = async (trustMaster) => {
    try {
      const response = await trustMastersAPI.previewReceipt(trustMaster.id);

      // Extract HTML content from response
      const htmlContent = response.data?.html || response.html || '';

      setPreviewContent(htmlContent);
      setPreviewTitle(`Receipt Preview - ${trustMaster.trustName || trustMaster.trust_name}`);
      setPreviewFilename(
        `receipt-preview-${(trustMaster.trustName || trustMaster.trust_name || 'trust')
          .toLowerCase()
          .replace(/\s+/g, '-')}-${Date.now()}.pdf`,
      );
      setPreviewTrustMasterId(trustMaster.id);
      setPreviewModalOpen(true);
    } catch (error) {
      console.error('Error loading receipt preview:', error);
      showError(error?.error?.message || 'Failed to load receipt preview');
    }
  };

  /**
   * Handle PDF download from backend
   */
  const handleDownloadPDF = async () => {
    if (!previewTrustMasterId) return;
    try {
      const blobData = await trustMastersAPI.downloadReceiptPdf(previewTrustMasterId);
      // blobData is already raw data from the response interceptor — wrap in a proper typed Blob
      const blob = new Blob([blobData], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = previewFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showError('Failed to download receipt PDF');
    }
  };

  /**
   * Handle preview modal close
   */
  const handlePreviewClose = () => {
    setPreviewModalOpen(false);
    setPreviewContent('');
    setPreviewTitle('');
    setPreviewFilename('');
    setPreviewTrustMasterId(null);
  };

  return (
    <Box>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} mt={1}>
          <Typography
            variant="h4"
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              color: colors.text.primary,
            }}
          >
            Trusts
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<FiPlus />}
          onClick={handleCreateNew}
          sx={{
            borderRadius: '4px',
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            backgroundColor: colors.primary.main,
            '&:hover': {
              backgroundColor: colors.primary.dark,
            },
          }}
        >
          Create Trust
        </Button>
      </Box>

      {/* Trust Master List */}
      <TrustMasterList
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        onPreviewReceipt={handlePreviewReceipt}
        refreshTrigger={refreshTrigger}
      />

      {/* Preview Modal */}
      <PreviewModal
        open={previewModalOpen}
        onClose={handlePreviewClose}
        content={previewContent}
        title={previewTitle}
        downloadFilename={previewFilename}
        onDownloadPDF={handleDownloadPDF}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName="trust master"
        deleting={deleting}
      />
    </Box>
  );
}
